import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import React, { memo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useLocation, useNavigate } from 'react-router';

import routeConfiguration, { ACCOUNT_SETTINGS_PAGES } from '../../../routeConfiguration';
import { petrol, yellow } from '../../../styles/mui/colors';
import useAppSelector from '../../../util/hooks/useAppSelector';
import { createResourceLocatorString, matchPathname } from '../../../util/routes';
import BoxSeparator from '../../BoxSeperator/BoxSeparator';
import IconAdd from '../../Icons/IconAdd';
import IconBookings from '../../Icons/IconBookings';
import IconCalendar from '../../Icons/IconCalendar';
import IconCard from '../../Icons/IconCard';
import IconMap from '../../Icons/IconMap';
import IconPin from '../../Icons/IconPin';
import IconPoints from '../../Icons/IconPoints';
import IconSettings from '../../Icons/IconSettings';
import IconUser from '../../Icons/IconUser';

const menuItemSpacing = 0;

const SectionNavigationComponent = ({ onClose, setSettingsMenuOpen }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const intl = useIntl();

  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);

  const currentPage = matchPathname(location.pathname, routeConfiguration())[0].route.name;
  const annualPassIsValid = useAppSelector((state) => state.PassSettingsPage.annualPassIsValid);
  const currentPageColor = (page) => {
    const isAccountSettingsPage =
      page === 'AccountSettingsPage' && ACCOUNT_SETTINGS_PAGES.includes(currentPage);
    return currentPage === page || isAccountSettingsPage ? yellow.main : petrol.main;
  };

  const navigateToPage = (pageName, routeParams = {}) => {
    onClose();
    const url = createResourceLocatorString(
      pageName,
      routeConfiguration(),
      routeParams, // <--- Pass these params here
      {},
      intl.locale,
    );
    navigate(url);
  };

  return isAuthenticated ? (
    <>
      <BoxSeparator />
      <Stack alignItems="flex-start" px={4} spacing={menuItemSpacing} width="100%">
        <Typography gutterBottom variant="h5">
          <FormattedMessage id="AccountBottomBarBox.account" />
        </Typography>
        <Button
          color="secondary"
          onClick={() => navigateToPage('ProfileSettingsPage')}
          startIcon={<IconUser color={currentPageColor('ProfileSettingsPage')} />}
          sx={{ color: currentPageColor('ProfileSettingsPage') }}
          variant="text"
        >
          <FormattedMessage id="TopbarMobileMenu.profileSettingsLink" />
        </Button>
        <Button
          color="secondary"
          onClick={() => navigateToPage('LoyaltyProgrammePage')}
          startIcon={<IconPoints color={currentPageColor('LoyaltyProgrammePage')} />}
          sx={{ color: currentPageColor('LoyaltyProgrammePage') }}
          variant="text"
        >
          <FormattedMessage id="TopbarMobileMenu.loyaltyProgrammeLink" />
        </Button>
        <Button
          color="secondary"
          onClick={() => setSettingsMenuOpen(true)} // Open the submenu
          startIcon={<IconSettings color={currentPageColor('AccountSettingsPage')} />}
          sx={{ color: currentPageColor('AccountSettingsPage') }}
          variant="text"
        >
          <FormattedMessage id="TopbarMobileMenu.accountSettingsLink" />
        </Button>
        {annualPassIsValid ? (
          <Button
            color="secondary"
            onClick={() => navigateToPage('PassSettingsPage')}
            startIcon={<IconCard color={currentPageColor('PassSettingsPage')} />}
            sx={{ color: currentPageColor('PassSettingsPage') }}
            variant="text"
          >
            <FormattedMessage id="TopbarDesktop.yourAnnualPassLink" />
          </Button>
        ) : null}
        <Button
          color="secondary"
          onClick={() => navigateToPage('InboxPage', { tab: 'orders' })}
          startIcon={<IconBookings sx={{ color: currentPageColor('InboxPage') }} />}
          sx={{ color: currentPageColor('InboxPage') }}
          variant="text"
        >
          <FormattedMessage id="InboxPage.heading" />
        </Button>
        <Button
          color="secondary"
          onClick={() => navigateToPage('CalendarPage')}
          startIcon={<IconCalendar color={currentPageColor('CalendarPage')} />}
          sx={{ color: currentPageColor('CalendarPage') }}
          variant="text"
        >
          <FormattedMessage id="TopbarMobileMenu.yourCalendarLink" />
        </Button>
        <Button
          color="secondary"
          onClick={() => navigateToPage('RoutePlannerPage')}
          startIcon={<IconMap color={currentPageColor('RoutePlannerPage')} />}
          sx={{ color: currentPageColor('RoutePlannerPage') }}
          variant="text"
        >
          <FormattedMessage id="TopbarDesktop.myTripsLink" />
          <Chip
            label="Beta"
            size="small"
            sx={{
              ml: 1,
              height: 18,
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: 0.5,
              bgcolor: 'secondary.main',
              color: 'secondary.contrastText',
              '& .MuiChip-label': { px: 0.75 },
            }}
          />
        </Button>
        <Button
          color="secondary"
          onClick={() => navigateToPage('ManageListingsPage')}
          startIcon={<IconPin color={currentPageColor('ManageListingsPage')} />}
          sx={{ color: currentPageColor('ManageListingsPage') }}
          variant="text"
        >
          <FormattedMessage id="BottomNavigationBar.listings" />
        </Button>
        <Button
          color="secondary"
          onClick={() => navigateToPage('NewListingPage')}
          startIcon={<IconAdd />}
          sx={{ color: currentPageColor('NewListingPage') }}
          variant="text"
        >
          <FormattedMessage id="MobileMenu.addSpot" />
        </Button>
      </Stack>
    </>
  ) : null;
};

SectionNavigationComponent.displayName = 'SectionNavigation';
const SectionNavigation = memo(SectionNavigationComponent);
export default SectionNavigation;
